<template>
  <section class="height-100-p">
    <el-table
      id="shuttle-unit-data-list"
      :data="shuttleUnit.list"
      v-loading="shuttleUnit.loadingSearch"
      size="mini"
      tooltip-effect="dark"
      class="dark-blue-table"
      :empty-text="$t('common.notAbleToFindRecords')"
      @sort-change="handleSortChange"
      :default-sort = "{prop: 'unit', order: 'descending'}"
      sortable
      stripe
    >
      <el-table-column
        prop="unit"
        :label="$t('outbound.shuttleUnit.unit')"
        :min-width="5"
        align="center"
        sortable="custom"
      >
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.unit" placement="top">
            <span> {{ scope.row.unit }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="dayOfService"
        :label="$t('outbound.shuttleUnit.dos')"
        :min-width="3"
        align="center"
        sortable="custom"
      >
        <template slot-scope="scope">
          <span> {{ scope.row.dayOfService | moment(dateDisplayFormat) }} </span>
        </template>
      </el-table-column>
      <el-table-column
        prop="shipper"
        :label="$t('outbound.shuttleUnit.shipper')"
        :min-width="5"
        align="left"
        header-align="center"
        sortable="custom"
      >
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.shipper" placement="top">
            <span> {{ scope.row.shipper }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="consignee"
        :label="$t('outbound.shuttleUnit.consignee')"
        :min-width="5"
        align="left"
        header-align="center"
        sortable="custom"
      >
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.consignee" placement="top">
            <span> {{ scope.row.consignee }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="warehouseCode"
        :label="$t('outbound.shuttleUnit.receivingWarehouse')"
        :min-width="3"
        align="center"
        sortable="custom"
      >
        <template slot-scope="scope">
          <el-tooltip v-if="scope.row.warehouseCode" class="item" effect="dark" :content="scope.row.warehouseName" placement="top">
            <span> {{ scope.row.warehouseCode }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="outboundWarehouseCode"
        :label="$t('outbound.shuttleUnit.outboundWarehouse')"
        :min-width="3"
        align="center"
        sortable="custom"
      >
        <template slot-scope="scope">
          <el-tooltip v-if="scope.row.outboundWarehouseCode" class="item" effect="dark" :content="scope.row.outboundWarehouseName" placement="top">
            <span> {{ scope.row.outboundWarehouseCode }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="receivedTime"
        :label="$t('outbound.shuttleUnit.receivedTime')"
        :min-width="4"
        align="center"
        sortable="custom">
      </el-table-column>
      <el-table-column
        prop="shuttled"
        :label="$t('outbound.shuttleUnit.shuttled')"
        :min-width="2"
        align="center"
        sortable="custom"
      >
        <template slot-scope="scope">
          <i v-if="scope.row.shuttled" class="el-icon-success green-success"></i>
        </template>
      </el-table-column>
      <el-table-column
        prop="shuttleNumber"
        :label="$t('outbound.shuttleUnit.shuttleNumber')"
        :min-width="3"
        align="center"
        sortable="custom"
      >
        <template slot-scope="scope">
          <el-tooltip v-if="scope.row.shuttleNumber" class="item" effect="dark" :content="scope.row.shuttleNumber" placement="top">
            <span> {{ scope.row.shuttleNumber }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="shuttleTime"
        :label="$t('outbound.shuttleUnit.shuttleTime')"
        :min-width="4"
        align="center"
        sortable="custom">
      </el-table-column>
      <el-table-column
        prop="putAwayCode"
        :label="$t('outbound.shuttleUnit.putAwayId')"
        :min-width="3"
        align="center"
        sortable="custom"
      >
        <template slot-scope="scope">
          <el-tooltip v-if="scope.row.putAwayCode" class="item" effect="dark" :content="scope.row.putAwayCode" placement="top">
            <span> {{ scope.row.putAwayCode }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="status"
        :label="$t('outbound.shuttleUnit.status')"
        :min-width="3"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <span> {{ statusLabel(scope.row.status, (scope.row.shuttleLoadId && scope.row.shuttleLoadId > 0)) }}</span>
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination-footer" v-show="shuttleUnit.total > 0">
      <span class="el-pagination__total">
        <span class="el-pagination__title">{{ $t('outbound.shuttleUnit.totalLabel') }}</span>
           {{ shuttleUnit.total }}
      </span>
      <el-pagination
        @size-change="handleSearchChangeLimit"
        :page-sizes="sizes"
        :page-size="shuttleUnit.actualFilters.rows"
        :current-page.sync="shuttleUnit.actualFilters.page"
        @current-change="handleSearchChangePage"
        :total="shuttleUnit.total"
        layout="sizes, prev, pager, next, jumper"
      >
      </el-pagination>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import constants from '@/utils/constants';
import unitDataTransformLabels from '@/plugins/unitDataTransformLabels';
import { SHUTTLE_UNIT_SEARCH } from '@/store/actions';

export default {
  name: 'ShuttleUnitSummarySection',
  mixins: [unitDataTransformLabels],
  computed: {
    ...mapGetters(['shuttleUnit']),
    sizes() {
      return constants.TABLES.DEFAULT_LIMIT_SIZES;
    },
    dateDisplayFormat() {
      return constants.DATES.DEFAULT_DISPLAY_FORMAT;
    }
  },
  methods: {
    ...mapActions({
      search: SHUTTLE_UNIT_SEARCH,
    }),
    handleSearchChangeLimit(rows) {
      this.search({ rows, page: constants.TABLES.DEFAULT_PAGE });
    },
    handleSearchChangePage(page) {
      this.search({ page });
    },
    handleSortChange({ prop, order }) {
      this.search({ orderField: prop, orderDirection: constants.TABLES.ORDER_DIRECTION[order] });
    },
  }
};
</script>
